import {
    Dialog,
    Typography,
    TextField,
    MenuItem,
    styled,
    Box,
    Button,
    Grid,
    CircularProgress,
} from '@mui/material';
import { Add, Cancel } from '@mui/icons-material';
import {
    MouseEvent, useState, useEffect, useRef,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { generatePath, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { RELEVANCE_TEST_DEFINITION_DETAILS } from 'src/lib/routes';
import { createRelevanceTestDefinition } from 'src/mutations/createRelevanceTestDefinition';
import { createRelevanceTestRun } from 'src/mutations/createRelevanceTestRun';
import { QUERY_KEY } from 'src/queries/queryRelevanceTestDefinitions';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { config } from 'src/lib/config';
import { CSSGrid } from 'src/components/common/CSSGrid';
import { DEFAULT_CULTURE } from 'src/constants';
import { getUserEmailQuery } from 'src/selectors/getUserEmailQuery';
import { SaveLoadingButton } from '../common/buttons/SaveLoadingButton';
import { useQueryProducts } from '../ProductSearchPage/hooks/useQueryProducts';

export interface PropTypes {
    open: boolean;
    onClose: () => void;
}

const LOCALE_LABEL = 'Select a locale';
const DEFAULT_SAMPLE_SIZE = 50;
const DEBOUNCE_DELAY = 1000; // 1 second debounce delay

const StyledDialog = styled(Dialog)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    '& .MuiPaper-root': {
        padding: theme.spacing(8),
        width: '100%',
        maxWidth: '600px',
    },
    '& .MuiDialog-container': {
        width: '100%',
    },
}));

const DialogContainer = styled(CSSGrid)(({ theme }) => ({
    gap: theme.spacing(4),
    overflow: 'hidden',
}));

const ActionButtons = styled(CSSGrid)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(4),
    justifyContent: 'flex-end',
    gridAutoFlow: 'column dense',
    marginTop: theme.spacing(4),
}));

const AlignCenter = styled(Box)({
    alignItems: 'center',
    display: 'inline-flex',
});

const sortedLocales = config.locales.sort();

export const CreateRelevanceTestDefinitionModal = ({
    open,
    onClose,
}: PropTypes): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const queryClient = useQueryClient();
    const [productKey, setProductKey] = useState('');
    const [debouncedProductKey, setDebouncedProductKey] = useState('');
    const [keywordSearch, setKeywordSearch] = useState('');
    const [culture, setCulture] = useState(DEFAULT_CULTURE);
    const [kSampleSize, setKSampleSize] = useState(DEFAULT_SAMPLE_SIZE);
    const [productName, setProductName] = useState<string | null>(null);
    const [productNotFound, setProductNotFound] = useState(false);
    const debounceTimerRef = useRef<number | null>(null);
    const navigate = useNavigate();
    const userEmail = useRecoilValue(getUserEmailQuery);

    const { data: productsData, isLoading: isLoadingProducts, isError } = useQueryProducts(
        accessToken,
        { productKey: debouncedProductKey || undefined },
        {
            enabled: !!debouncedProductKey && !!accessToken,
            suspense: false,
        },
    );

    const handleClose = (): void => {
        setProductKey('');
        setKeywordSearch('');
        setDebouncedProductKey('');
        setProductName(null);
        setProductNotFound(false);
        onClose();
    };

    useEffect(() => {
        if (debounceTimerRef.current) {
            window.clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = window.setTimeout(() => {
            setDebouncedProductKey(productKey);
        }, DEBOUNCE_DELAY);

        if (productKey !== debouncedProductKey) {
            setProductNotFound(false);
        }

        return () => {
            if (debounceTimerRef.current) {
                window.clearTimeout(debounceTimerRef.current);
            }
        };
    }, [productKey, debouncedProductKey]);

    useEffect(() => {
        if (!debouncedProductKey) {
            setProductName(null);
            setProductNotFound(false);
            return;
        }

        if (isError) {
            setProductName(null);
            setProductNotFound(true);
            return;
        }

        if (productsData && productsData.result && productsData.result.length > 0) {
            const firstProduct = productsData.result[0].matching_versions[0];

            setProductName(firstProduct.productName);
            setProductNotFound(false);
        } else if (!isLoadingProducts && debouncedProductKey) {
            setProductName(null);
            setProductNotFound(true);
        }
    }, [productsData, debouncedProductKey, isLoadingProducts, isError]);

    const handleProductKeyChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setProductKey(event.target.value);

        if (!event.target.value) {
            setDebouncedProductKey('');
            setProductName(null);
            setProductNotFound(false);
        }
    };

    const handleKeywordSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setKeywordSearch(event.target.value);
    };

    const handleCultureChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        event.preventDefault();
        setCulture(event.target.value);
    };

    const handleSampleSizeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const number = parseInt(event.target.value, 10);

        if (!Number.isNaN(number)) {
            setKSampleSize(number);
        }
    };

    const createDefinitionMutation = useMutation<
    Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition,
    Error,
    App.RelevanceTestDefinitions.CreateMutationVariables
    >(
        createRelevanceTestDefinition,
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QUERY_KEY]);
            },
            useErrorBoundary: true,
        },
    );

    const runTestMutation = useMutation(
        createRelevanceTestRun,
        {
            useErrorBoundary: false,
        },
    );

    const handleNewRelevanceTestDefintion = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
        event.preventDefault();

        if (accessToken && productName) {
            const newRelevanceTestDefinition = await createDefinitionMutation.mutateAsync({
                accessToken,
                data: {
                    productKey,
                    keywordSearchTerm: keywordSearch,
                    productName,
                    culture,
                    kSampleSize,
                    createdBy: userEmail,
                },
            });

            await runTestMutation.mutateAsync({
                accessToken,
                testDefinitionId: newRelevanceTestDefinition.testDefinitionId,
            });

            navigate(generatePath(RELEVANCE_TEST_DEFINITION_DETAILS, {
                testDefinitionId: newRelevanceTestDefinition.testDefinitionId,
            }));

            handleClose();
        }
    };

    const isSearching = productKey !== '' && (isLoadingProducts || (productKey !== debouncedProductKey && productKey !== ''));
    const isCreating = createDefinitionMutation.isLoading || runTestMutation.isLoading;

    return (
        <StyledDialog
            open={open}
            onClose={handleClose}
        >
            <DialogContainer>
                <AlignCenter>
                    <Typography variant="h6">
                        Create New Relevance Test
                    </Typography>
                </AlignCenter>
                <Typography variant="caption">
                    Create a new relevance test by providing a keyword search, product key,
                    culture, and sample size. You will be navigated to the test definition details page after creation.
                </Typography>
                <Grid container gap={4}>
                    <TextField
                        fullWidth
                        required
                        id="keyword-search-"
                        label="Keyword Search"
                        size="small"
                        value={keywordSearch}
                        onChange={handleKeywordSearchChange}
                    />
                    <TextField
                        fullWidth
                        required
                        error={productNotFound}
                        helperText={productNotFound ? 'Product not found. Please verify the product key.' : ''}
                        id="product-key"
                        InputProps={{
                            endAdornment: isSearching ? (
                                <CircularProgress color="inherit" size={20} />
                            ) : null,
                        }}
                        label="Product Key (PRD)"
                        size="small"
                        value={productKey}
                        onChange={handleProductKeyChange}
                    />
                    <TextField
                        disabled
                        fullWidth
                        helperText={!productKey ? 'Enter a product key to see the product name' : ''}
                        id="product-name"
                        InputProps={{
                            endAdornment: isSearching ? (
                                <CircularProgress color="inherit" size={20} />
                            ) : null,
                            readOnly: true,
                        }}
                        label="Product Name"
                        size="small"
                        value={productName || ''}
                    />
                    <TextField
                        fullWidth
                        required
                        select
                        aria-label={LOCALE_LABEL}
                        label={LOCALE_LABEL}
                        size="small"
                        value={culture}
                        onChange={handleCultureChange}
                    >
                        {sortedLocales.map((item) => (
                            <MenuItem key={item} value={item}>
                                {item}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        fullWidth
                        required
                        id="k-sample-size"
                        label="Sample Size"
                        size="small"
                        type="number"
                        value={kSampleSize}
                        onChange={handleSampleSizeChange}
                    />
                </Grid>
                <ActionButtons>
                    <Button aria-label="cancel" color="secondary" startIcon={<Cancel />} variant="outlined" onClick={handleClose}>
                        Cancel
                    </Button>
                    <SaveLoadingButton
                        aria-label="create new relevance test"
                        disabled={!(
                            keywordSearch && productKey && culture && kSampleSize && productName && !productNotFound
                        ) || isCreating}
                        Icon={Add}
                        variant="contained"
                        onClick={handleNewRelevanceTestDefintion}
                    >
                        {isCreating ? 'Creating...' : 'Create'}
                    </SaveLoadingButton>
                </ActionButtons>
            </DialogContainer>
        </StyledDialog>
    );
};
